import React, { useCallback } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WorkflowHiringNotificationsDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-notifications/workflow-hiring-notifications';

export interface ConfigureHiringNotificationsDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowHiringNotificationsDetails | null;
  updateDetails: (details: WorkflowHiringNotificationsDetails | null) => void;
}

export const ConfigureHiringNotificationsDetails = ({
  details,
  updateDetails,
}: ConfigureHiringNotificationsDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowHiringNotificationsDetails>) => {
      updateDetails({
        type: 'hiringNotifications',
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'hiringNotifications',
          },
    );
  }, [details, updateDetails]);

  const updatePreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  const updateMinReadinessRating = useCallback(
    (minReadinessRating: number | undefined) => {
      updateDetailsPartial({
        minReadinessRating:
          minReadinessRating !== undefined && isNaN(minReadinessRating)
            ? undefined
            : minReadinessRating,
      });
    },
    [updateDetailsPartial],
  );

  const updateForceOnlySendToThisEmail = useCallback(
    (forceOnlySendToThisEmail: string) => {
      updateDetailsPartial({
        forceOnlySendToThisEmail:
          forceOnlySendToThisEmail.trim() === ''
            ? undefined
            : forceOnlySendToThisEmail,
      });
    },
    [updateDetailsPartial],
  );

  const updateRecipientEmails = useCallback(
    (recipientEmailsRaw: string) => {
      updateDetailsPartial({
        recipientEmails:
          recipientEmailsRaw.trim() === ''
            ? undefined
            : recipientEmailsRaw.split(',').map((s) => s.trim()),
      });
    },
    [updateDetailsPartial],
  );

  const toggleResumeScoreEnabled = useCallback(() => {
    updateDetailsPartial({
      resumeScoreEnabled: details?.resumeScoreEnabled ? undefined : true,
    });
  }, [details?.resumeScoreEnabled, updateDetailsPartial]);

  const updateMinResumeScore = useCallback(
    (minResumeScore: number | undefined) => {
      updateDetailsPartial({
        minResumeScore:
          minResumeScore !== undefined && isNaN(minResumeScore)
            ? undefined
            : minResumeScore,
      });
    },
    [updateDetailsPartial],
  );

  const updateMaxNewCandidatesPerEmail = useCallback(
    (maxNewCandidatesPerEmail: number | undefined) => {
      updateDetailsPartial({
        maxNewCandidatesPerEmail:
          maxNewCandidatesPerEmail !== undefined &&
          isNaN(maxNewCandidatesPerEmail)
            ? undefined
            : maxNewCandidatesPerEmail,
      });
    },
    [updateDetailsPartial],
  );

  const updateRevisitWindowDays = useCallback(
    (revisitWindowDays: number | undefined) => {
      updateDetailsPartial({
        revisitWindowDays:
          revisitWindowDays !== undefined && isNaN(revisitWindowDays)
            ? undefined
            : revisitWindowDays,
      });
    },
    [updateDetailsPartial],
  );

  const updateMaxRevisitCandidatesShown = useCallback(
    (maxRevisitCandidatesShown: number | undefined) => {
      updateDetailsPartial({
        maxRevisitCandidatesShown:
          maxRevisitCandidatesShown !== undefined &&
          isNaN(maxRevisitCandidatesShown)
            ? undefined
            : maxRevisitCandidatesShown,
      });
    },
    [updateDetailsPartial],
  );

  const updateAtsName = useCallback(
    (atsName: string) => {
      updateDetailsPartial({
        atsName: atsName.trim() === '' ? undefined : atsName,
      });
    },
    [updateDetailsPartial],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">Configure Hiring Notifications</h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleEnabled}>
        Enable hiring notifications
      </S2Checkbox>
      {details && (
        <>
          <label className="space-y-1 flex flex-col">
            <span>Number of previous days of applicants</span>

            <input
              type="text"
              value={details.numPreviousDaysOfApplicants ?? ''}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updatePreviousDaysOfApplicants(numericValue);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <label className="space-x-1 flex flex-col">
            <span>
              Minimum readiness rating for hiring notification summary email
            </span>

            <input
              type="number"
              className="form-input"
              value={details.minReadinessRating ?? ''}
              min={1}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updateMinReadinessRating(numericValue);
              }}
            />
          </label>

          <label className="space-x-1 flex flex-col">
            <span>
              Force hiring notification summary to only send to this email
            </span>

            <span className="text-gray-700">
              IMPORTANT: only use when debugging since this will override the
              manager email
            </span>

            <input
              className="form-input"
              type="email"
              value={details.forceOnlySendToThisEmail ?? ''}
              onChange={(e) => {
                updateForceOnlySendToThisEmail(e.target.value);
              }}
            />
          </label>

          <label className="space-x-1 flex flex-col">
            <span>Recipient emails</span>

            <span className="text-gray-700">
              Comma-separated list of emails to send notification summaries to,
              e.g. hello@world.com,hola@mundo.com
            </span>

            <textarea
              rows={2}
              className="form-textarea"
              value={
                details.recipientEmails
                  ? details.recipientEmails.join(',')
                  : undefined
              }
              onChange={(e) => {
                updateRecipientEmails(e.target.value);
              }}
            />
          </label>

          <S2Checkbox
            checked={Boolean(details.resumeScoreEnabled)}
            onChange={toggleResumeScoreEnabled}
          >
            Include candidates with high resume score (in addition to readiness
            rating)
          </S2Checkbox>

          {details.resumeScoreEnabled && (
            <label className="space-x-1 flex flex-col">
              <span>Minimum resume score</span>
              <input
                type="number"
                className="form-input"
                value={details.minResumeScore ?? ''}
                min={0}
                step={0.1}
                onChange={(e) => {
                  updateMinResumeScore(parseFloat(e.target.value));
                }}
              />
            </label>
          )}

          <label className="space-x-1 flex flex-col">
            <span>Max new candidates per email (default: 10)</span>
            <input
              type="number"
              className="form-input"
              value={details.maxNewCandidatesPerEmail ?? ''}
              min={1}
              onChange={(e) => {
                updateMaxNewCandidatesPerEmail(parseInt(e.target.value, 10));
              }}
            />
          </label>

          <label className="space-x-1 flex flex-col">
            <span>Revisit window (days, default: 7)</span>
            <span className="text-gray-700">
              Previously-notified candidates appear in &ldquo;You may have
              missed&rdquo; for this many days
            </span>
            <input
              type="number"
              className="form-input"
              value={details.revisitWindowDays ?? ''}
              min={1}
              onChange={(e) => {
                updateRevisitWindowDays(parseInt(e.target.value, 10));
              }}
            />
          </label>

          <label className="space-x-1 flex flex-col">
            <span>Max revisit candidates shown per email (default: 5)</span>
            <input
              type="number"
              className="form-input"
              value={details.maxRevisitCandidatesShown ?? ''}
              min={1}
              onChange={(e) => {
                updateMaxRevisitCandidatesShown(parseInt(e.target.value, 10));
              }}
            />
          </label>

          <SelectBoxWithSearch
            type="single"
            list={[
              { optionLabel: 'Not set (defaults to "ATS")', optionValue: '' },
              { optionLabel: 'ADP', optionValue: 'ADP' },
              { optionLabel: 'Lever', optionValue: 'Lever' },
            ]}
            label="ATS name (shown in candidate links)"
            selected={details.atsName ?? ''}
            setSelected={updateAtsName}
          />
        </>
      )}
    </div>
  );
};
